/**
 * Shared utilities for lap calculations across Charts and Tyre Plots
 */
import { combinedInactiveLaps } from 'types/Run';

/**
 * A run, as far as lap/traverse maths is concerned: the per-traverse channel arrays
 * plus the lap markers that say which of those traverses are excluded.
 */
export interface TraverseRun {
  traverse?: Record<string, (number | null)[] | null> | null;
  lap_markers?: { inactive_laps?: boolean[] | null; inactive_laps_auto?: boolean[] | null } | null;
}

/**
 * One run's per-traverse values with excluded traverses replaced by `null`.
 *
 * `run.traverse[key]` always arrives full-length — the server keeps every traverse on
 * the wire so the results table can render (and toggle) the excluded ones — and only
 * the *averages* have the active-lap mask applied. Anything plotting the raw arrays
 * therefore has to apply the mask itself.
 *
 * The mask is index-aligned with the traverse arrays; entries past the end of the mask
 * count as active, mirroring `LapMarkers.get_active_lap_mask()` on the server, which
 * pads the shorter of its two author masks with "active". Nulling rather than dropping
 * keeps the indices intact so the pairing below still pairs an outbound traverse with
 * its own return.
 */
export function maskedRunTraverses(run: TraverseRun, key: string): (number | null)[] {
  const values = run?.traverse?.[key] ?? [];
  const inactive = combinedInactiveLaps(run?.lap_markers);
  return values.map((value, index) => (value && !inactive[index] ? value : null));
}

/**
 * Laps from a run's masked traverse values. Groups traverses exactly as
 * `calculateLaps` does, but averages only the *active* members of each group, and
 * drops a group entirely when every traverse in it is excluded.
 *
 * Averaging the survivors rather than discarding a half-excluded pair is what keeps
 * the plot agreeing with the numbers above it: the config/run averages the server
 * returns are plain means over the active traverses, so a pair whose return leg was
 * excluded contributes its outbound leg to both. Discarding it instead would empty
 * the plot for the common case of a session where one direction is excluded
 * throughout, while the average line stayed put.
 */
export function calculateLapsMasked(traverses: (number | null)[], isTraverseSession: boolean): number[] {
  const laps: number[] = [];
  const numTraverses = traverses.length;

  if (numTraverses === 0) return laps;

  if (!isTraverseSession) {
    // For non-traverse sessions: every traverse is a lap (one-to-one)
    return traverses.filter((value): value is number => value != null);
  }

  const pushGroup = (group: (number | null)[]) => {
    const active = group.filter((value): value is number => value != null);
    if (active.length === 0) return;
    laps.push(active.reduce((sum, value) => sum + value, 0) / active.length);
  };

  if (numTraverses % 2 === 0) {
    // Even number: pair them as (0,1), (2,3), (4,5), etc.
    for (let i = 0; i < numTraverses; i += 2) {
      pushGroup([traverses[i], traverses[i + 1]]);
    }
  } else {
    // Odd number: pair them as (0,1), (2,3), etc., and use last 3 for final average
    for (let i = 0; i < numTraverses - 3; i += 2) {
      pushGroup([traverses[i], traverses[i + 1]]);
    }
    // Average the last 3, or the lone traverse when that is all there is
    pushGroup(traverses.slice(Math.max(numTraverses - 3, 0)));
  }

  return laps;
}

/**
 * The values one run contributes to a plot at the chosen granularity. Excluded
 * traverses never appear as points, and never contribute to a lap.
 */
export function activeRunValues(
  run: TraverseRun,
  key: string,
  dataType: 'traverses' | 'laps',
  isTraverseSession: boolean,
): number[] {
  const masked = maskedRunTraverses(run, key);
  if (dataType === 'laps') return calculateLapsMasked(masked, isTraverseSession);
  return masked.filter((value): value is number => value != null);
}

/**
 * Config-level values: laps/traverses are resolved per run and then concatenated, so a
 * traverse pair never straddles a run boundary.
 */
export function collectConfigValues(
  config: { runs?: TraverseRun[] | null } | null | undefined,
  key: string,
  dataType: 'traverses' | 'laps',
  isTraverseSession: boolean,
): number[] {
  const values: number[] = [];
  for (const run of config?.runs ?? []) {
    values.push(...activeRunValues(run, key, dataType, isTraverseSession));
  }
  return values;
}

/**
 * Calculate laps from traverses based on session type
 * @param traverses - Array of traverse values
 * @param isTraverseSession - Whether this is a traverse session (analysis_type >= 1000 && < 2000)
 * @returns Array of lap values
 */
export function calculateLaps(traverses: number[], isTraverseSession: boolean): number[] {
  const laps: number[] = [];
  const numTraverses = traverses.length;

  if (numTraverses === 0) return laps;

  if (isTraverseSession) {
    // For traverse sessions: a lap is a traverse pair
    if (numTraverses % 2 === 0) {
      // Even number: pair them as (0,1), (2,3), (4,5), etc.
      for (let i = 0; i < numTraverses; i += 2) {
        const lapAvg = (traverses[i] + traverses[i + 1]) / 2;
        laps.push(lapAvg);
      }
    } else {
      // Odd number: pair them as (0,1), (2,3), etc., and use last 3 for final average
      for (let i = 0; i < numTraverses - 3; i += 2) {
        const lapAvg = (traverses[i] + traverses[i + 1]) / 2;
        laps.push(lapAvg);
      }
      // Average the last 3
      if (numTraverses >= 3) {
        const lastThreeAvg =
          (traverses[numTraverses - 3] + traverses[numTraverses - 2] + traverses[numTraverses - 1]) / 3;
        laps.push(lastThreeAvg);
      } else if (numTraverses === 1) {
        // If only 1 traverse, just use it
        laps.push(traverses[0]);
      }
    }
  } else {
    // For non-traverse sessions: every traverse is a lap (one-to-one)
    laps.push(...traverses);
  }

  return laps;
}

/**
 * Calculate lap timestamps for time-based plots
 * For each lap (traverse pair), calculate the midpoint timestamp
 * @param traverseTimestamps - Array of traverse timestamps (in milliseconds)
 * @param isTraverseSession - Whether this is a traverse session
 * @returns Array of lap timestamps
 */
export function calculateLapTimestamps(traverseTimestamps: number[], isTraverseSession: boolean): number[] {
  const lapTimestamps: number[] = [];
  const numTraverses = traverseTimestamps.length;

  if (numTraverses === 0) return lapTimestamps;

  if (isTraverseSession) {
    // For traverse sessions: calculate midpoint between paired traverses
    if (numTraverses % 2 === 0) {
      // Even number: pair them as (0,1), (2,3), (4,5), etc.
      for (let i = 0; i < numTraverses; i += 2) {
        const midpoint = (traverseTimestamps[i] + traverseTimestamps[i + 1]) / 2;
        lapTimestamps.push(midpoint);
      }
    } else {
      // Odd number: pair them as (0,1), (2,3), etc., and use midpoint of last 3
      for (let i = 0; i < numTraverses - 3; i += 2) {
        const midpoint = (traverseTimestamps[i] + traverseTimestamps[i + 1]) / 2;
        lapTimestamps.push(midpoint);
      }
      // Midpoint of last 3
      if (numTraverses >= 3) {
        const midpoint = traverseTimestamps[numTraverses - 2]; // Use middle timestamp
        lapTimestamps.push(midpoint);
      } else if (numTraverses === 1) {
        // If only 1 traverse, just use it
        lapTimestamps.push(traverseTimestamps[0]);
      }
    }
  } else {
    // For non-traverse sessions: every traverse timestamp is a lap timestamp (one-to-one)
    lapTimestamps.push(...traverseTimestamps);
  }

  return lapTimestamps;
}
